import { useEventInterest } from "@/hooks/use-event-interest";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult } from "@tanstack/react-query";
import { keyWithPrincipal } from "@/queries/principal-key";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import {
    CreateWarmPoolRequest,
    DeleteWarmPoolRequest,
    ListWarmPoolsRequest,
    ListWarmPoolsRequest_Filter,
    UpdateWarmPoolRequest,
    WarmPoolPhase,
    type WarmPool,
} from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";

export const WARM_POOL_MIN_SIZE = 1;
export const WARM_POOL_MAX_SIZE = 20;
export const WARM_POOL_DEFAULT_SIZE = 1;

export type WarmPoolLocalEntry = {
    enabled: boolean;
    size: number;
    mutating: boolean;
};

/**
 * Returns true if the warm pool is marked for deletion or already deleted.
 * Checks both spec.desiredPhase (API intent) and status.phase (runner state)
 * because the runner may not have processed the deletion yet.
 */
export function isWarmPoolDeleting(pool: PlainWarmPool): boolean {
    const statusPhase = pool.status?.phase;
    const desiredPhase = pool.spec?.desiredPhase;
    return (
        statusPhase === WarmPoolPhase.DELETING ||
        statusPhase === WarmPoolPhase.DELETED ||
        desiredPhase === WarmPoolPhase.DELETING ||
        desiredPhase === WarmPoolPhase.DELETED
    );
}

export const warmPoolQueryKeys = {
    listForProject: (projectId?: string) => keyWithPrincipal(["warmPools", "list", { projectId }]),
    listForRunner: (runnerId?: string) => keyWithPrincipal(["warmPools", "list", { runnerId }]),
};

export type PlainWarmPool = PlainMessage<WarmPool>;

const toPlainWarmPool = (warmPool: WarmPool): PlainWarmPool => toPlainMessage(warmPool);

/**
 * Options hook for listing warm pools for a project. Returns a map of
 * environmentClassId -> WarmPool for easy lookup when rendering per-class state.
 */
export const useProjectWarmPoolsOptions = (projectId?: string, enabled = true) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: warmPoolQueryKeys.listForProject(projectId),
        queryFn: async () => {
            if (!projectId) {
                return new Map<string, PlainWarmPool>();
            }

            const { warmPools } = await api.prebuildService.listWarmPools(
                new ListWarmPoolsRequest({
                    filter: new ListWarmPoolsRequest_Filter({
                        projectIds: [projectId],
                    }),
                }),
            );

            const byEnvClass = new Map<string, PlainWarmPool>();
            for (const pool of warmPools) {
                const envClassId = pool.metadata?.environmentClassId;
                if (envClassId) {
                    byEnvClass.set(envClassId, toPlainWarmPool(pool));
                }
            }
            return byEnvClass;
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!projectId && enabled,
        staleTime: 1_000 * 30,
        gcTime: 1_000 * 60 * 5,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
    });
};

export const useProjectWarmPools = (projectId?: string, enabled = true) => {
    useEventInterest({ resourceType: ResourceType.WARM_POOL });
    return useQuery(useProjectWarmPoolsOptions(projectId, enabled));
};

/**
 * Options hook for listing warm pools filtered by runner.
 * Fetches all org warm pools and filters client-side by runnerId.
 */
export const useRunnerWarmPoolsOptions = (runnerId?: string, enabled = true) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: warmPoolQueryKeys.listForRunner(runnerId),
        queryFn: async () => {
            if (!runnerId) {
                return [];
            }

            const { warmPools } = await api.prebuildService.listWarmPools(new ListWarmPoolsRequest({}));

            return warmPools
                .filter((pool) => pool.metadata?.runnerId === runnerId && !isWarmPoolDeleting(pool))
                .map(toPlainWarmPool);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!runnerId && enabled,
        staleTime: 1_000 * 30,
        gcTime: 1_000 * 60 * 5,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
    });
};

export const useRunnerWarmPools = (runnerId?: string, enabled = true) => {
    useEventInterest({ resourceType: ResourceType.WARM_POOL });
    return useQuery(useRunnerWarmPoolsOptions(runnerId, enabled));
};

export type CreateWarmPoolParams = {
    projectId: string;
    environmentClassId: string;
    desiredSize: number;
};

export const useCreateWarmPool = (): UseMutationResult<PlainWarmPool, Error, CreateWarmPoolParams> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateWarmPoolParams) => {
            const { warmPool } = await api.prebuildService.createWarmPool(
                new CreateWarmPoolRequest({
                    projectId: params.projectId,
                    environmentClassId: params.environmentClassId,
                    desiredSize: params.desiredSize,
                }),
            );
            if (!warmPool) {
                throw new Error("Failed to create warm pool");
            }
            return toPlainWarmPool(warmPool);
        },
        onSuccess: (_created, variables) => {
            void queryClient.invalidateQueries({
                queryKey: warmPoolQueryKeys.listForProject(variables.projectId),
            });
        },
    });
};

export type UpdateWarmPoolParams = {
    warmPoolId: string;
    projectId: string;
    desiredSize: number;
};

export const useUpdateWarmPool = (): UseMutationResult<PlainWarmPool, Error, UpdateWarmPoolParams> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateWarmPoolParams) => {
            const { warmPool } = await api.prebuildService.updateWarmPool(
                new UpdateWarmPoolRequest({
                    warmPoolId: params.warmPoolId,
                    desiredSize: params.desiredSize,
                }),
            );
            if (!warmPool) {
                throw new Error("Failed to update warm pool");
            }
            return toPlainWarmPool(warmPool);
        },
        onSuccess: (_updated, variables) => {
            void queryClient.invalidateQueries({
                queryKey: warmPoolQueryKeys.listForProject(variables.projectId),
            });
        },
    });
};

export type DeleteWarmPoolParams = {
    warmPoolId: string;
    projectId: string;
};

export const useDeleteWarmPool = (): UseMutationResult<void, Error, DeleteWarmPoolParams> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: DeleteWarmPoolParams) => {
            await api.prebuildService.deleteWarmPool(
                new DeleteWarmPoolRequest({
                    warmPoolId: params.warmPoolId,
                }),
            );
        },
        onSuccess: (_result, variables) => {
            void queryClient.invalidateQueries({
                queryKey: warmPoolQueryKeys.listForProject(variables.projectId),
            });
        },
    });
};

/**
 * Handles warm pool events from the WatchEvents stream.
 * Invalidates all warm pool list queries so the UI picks up
 * create/update/delete changes pushed by the server.
 */
export const handleWarmPoolEvent = async (client: QueryClient, _evt: WatchEventsResponse) => {
    await client.invalidateQueries({ queryKey: keyWithPrincipal(["warmPools", "list"]) });
};
